import debug from 'debug'
import { useEffect, useMemo } from 'react'
import { useGenerationModelGetSizeConfig } from 'src/services/orpc/sizeConfig'
import { deriveSidebarRatios, getDefaultSize } from './apiAdapter'

const log = debug('pixai:size-config')

export type SizeConfigResult = ReturnType<typeof useSizeConfig>

export function useSizeConfig(modelVersionId?: string) {
  const { data, isLoading, refetch } =
    useGenerationModelGetSizeConfig(modelVersionId)

  const ranges = useMemo(() => data?.ranges ?? [], [data])

  const sidebarRanges = useMemo(
    () => ranges.filter(r => r.displayMode !== 'hidden'),
    [ranges],
  )
  const sidebarRatios = useMemo(() => deriveSidebarRatios(ranges), [ranges])
  const defaultSize = useMemo(() => getDefaultSize(ranges), [ranges])

  const isApiDriven = ranges.length > 0
  /**
   * The API must always return at least one range for a valid model version.
   * An empty response means a server-side misconfiguration, not a valid state.
   * Skip when modelVersionId is missing (query is disabled, not failed).
   */
  const isError = !!modelVersionId && !isLoading && !data?.ranges?.length

  useEffect(() => {
    if (data?.ranges?.length) {
      log(
        'API loaded for model %s: %d ranges',
        modelVersionId,
        data.ranges.length,
      )
    } else if (isError) {
      log('API error for model %s', modelVersionId)
    }
  }, [data, isError, modelVersionId])

  return {
    ranges,
    sidebarRanges,
    sidebarRatios,
    defaultSize,
    isLoading,
    isApiDriven,
    isError,
    refetch,
  }
}
